import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Briefcase, GraduationCap, Landmark, Languages, MapPin, Mail, Phone, ScrollText } from "lucide-react";
import { Loading } from "@/components/common";
import { useTranslation } from "react-i18next";

export default function AttorneyProfile() {
  const { t } = useTranslation();
  const { profile } = useOutletContext() || {};
  if (!profile) return <Loading label={t("attorney.loading")} />;

  return (
    <div data-testid="attorney-page">
      <section className="bg-navy text-ivory hero-grain relative py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-5 gap-14 items-center relative z-10">
          <motion.div initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-2">
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-gold/60" />
              <img src={profile.photo_url} alt={profile.name} data-testid="attorney-photo"
                className="relative w-full h-[520px] object-cover object-top" />
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="lg:col-span-3">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold mb-4">{profile.firm_name}</p>
            <h1 className="font-serif text-5xl sm:text-6xl font-semibold tracking-tight">{profile.name}</h1>
            <p className="mt-3 text-lg text-ivory/70">{profile.title} · {t("common.yearsOfPractice", { count: profile.years_experience })}</p>
            <div className="gold-divider my-8" />
            <p className="text-ivory/80 leading-relaxed">{profile.about_short}</p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2 text-ivory/70"><Phone className="h-4 w-4 text-gold" /> {profile.phone}</div>
              <div className="flex items-center gap-2 text-ivory/70"><Mail className="h-4 w-4 text-gold" /> {profile.email}</div>
              <div className="flex items-center gap-2 text-ivory/70"><Languages className="h-4 w-4 text-gold" /> {(profile.languages || []).join(", ")}</div>
            </div>
            <Link to="/book-consultation" data-testid="attorney-book-btn"
              className="mt-10 inline-flex items-center gap-2 bg-gold text-navy hover:bg-gold-600 transition-colors duration-300 font-semibold uppercase tracking-wider text-sm px-8 py-4">
              {t("attorney.schedule")} <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-3 gap-16">
          <div className="lg:col-span-2">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy mb-8">{t("attorney.biography")}</h2>
            <div className="space-y-5" data-testid="attorney-bio">
              {(profile.bio || "").split("\n\n").map((para, i) => (
                <p key={i} className="text-muted-foreground leading-relaxed">{para}</p>
              ))}
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy mt-16 mb-8 flex items-center gap-3">
              <Briefcase className="h-6 w-6 text-gold-700" /> {t("attorney.experience")}
            </h2>
            <div className="space-y-0 border-l-2 border-border" data-testid="attorney-experience">
              {(profile.experience || []).map((e) => (
                <div key={e.role} className="relative pl-8 pb-10">
                  <span className="absolute -left-[7px] top-1.5 h-3 w-3 bg-gold border-2 border-ivory" />
                  <p className="text-xs uppercase tracking-widest text-gold-700 font-semibold">{e.period}</p>
                  <h3 className="font-serif text-2xl font-semibold text-navy mt-1">{e.role}</h3>
                  <p className="text-sm font-semibold text-navy-700">{e.firm}</p>
                  <p className="text-sm text-muted-foreground mt-2">{e.detail}</p>
                </div>
              ))}
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-navy mt-8 mb-8 flex items-center gap-3">
              <GraduationCap className="h-6 w-6 text-gold-700" /> {t("attorney.education")}
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-testid="attorney-education">
              {(profile.education || []).map((e) => (
                <div key={e.degree} className="bg-white border border-border p-6 hover:border-gold transition-colors">
                  <p className="text-xs uppercase tracking-widest text-gold-700 font-semibold">{e.year}</p>
                  <h3 className="font-serif text-xl font-semibold text-navy mt-1">{e.degree}</h3>
                  <p className="text-sm text-navy-700 mt-1">{e.school}</p>
                  <p className="text-sm text-muted-foreground mt-2">{e.detail}</p>
                </div>
              ))}
            </div>
          </div>

          <aside className="space-y-10">
            <div className="bg-navy text-ivory p-8" data-testid="attorney-stats-card">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-gold mb-6">{t("attorney.atAGlance")}</h3>
              <dl className="space-y-5">
                {Object.entries({ [t("attorney.casesHandled")]: profile.stats?.cases_handled, [t("attorney.successRate")]: profile.stats?.success_rate, [t("attorney.recovered")]: profile.stats?.settlements_recovered }).map(([k, v]) => (
                  <div key={k} className="flex justify-between items-baseline border-b border-ivory/10 pb-4">
                    <dt className="text-sm text-ivory/60">{k}</dt>
                    <dd className="font-serif text-2xl font-semibold text-gold">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div data-testid="attorney-bar-admissions">
              <h3 className="font-serif text-2xl font-semibold text-navy mb-5 flex items-center gap-3">
                <ScrollText className="h-5 w-5 text-gold-700" /> {t("attorney.bar")}
              </h3>
              <ul className="space-y-3">
                {(profile.bar_admissions || []).map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-muted-foreground bg-white border border-border px-4 py-3">
                    <Landmark className="h-4 w-4 text-gold-700 mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="attorney-awards">
              <h3 className="font-serif text-2xl font-semibold text-navy mb-5 flex items-center gap-3">
                <Award className="h-5 w-5 text-gold-700" /> {t("attorney.awards")}
              </h3>
              <ul className="space-y-3">
                {(profile.awards || []).map((a) => (
                  <li key={a.title} className="bg-white border border-border px-4 py-3">
                    <p className="text-sm font-semibold text-navy">{a.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.year}</p>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border border-border p-6">
              <h3 className="font-serif text-xl font-semibold text-navy mb-3 flex items-center gap-2">
                <MapPin className="h-4 w-4 text-gold-700" /> {t("attorney.office")}
              </h3>
              <p className="text-sm text-muted-foreground">{profile.office_address}</p>
              <p className="text-sm text-muted-foreground mt-2">{profile.office_hours}</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
